/**
 * 欢迎项目创建器
 * 负责在新用户注册后创建示例项目
 */

import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../../lib/auth/AuthProvider';
import { 
  shouldCreateWelcomeProject, 
  createWelcomeProject,
  hasWelcomeProject 
} from '../../../utils/welcomeProjectUtils';

interface WelcomeProjectCreatorProps {
  onProjectCreated?: (projectId: string) => void;
  onError?: (error: Error) => void;
}

export const WelcomeProjectCreator: React.FC<WelcomeProjectCreatorProps> = ({
  onProjectCreated,
  onError
}) => {
  const { user, loading } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  // 使用 ref 存储回调函数的最新引用，避免 useEffect 重复执行
  const onProjectCreatedRef = useRef(onProjectCreated);
  const onErrorRef = useRef(onError);

  // 更新 ref 中的回调函数引用
  onProjectCreatedRef.current = onProjectCreated;
  onErrorRef.current = onError;

  useEffect(() => {
    const handleWelcomeProjectCreation = async () => {
      // 如果还在加载认证状态，等待
      if (loading) return;

      // 如果没有用户，不处理
      if (!user) return;

      // 如果已经检查过，不重复检查
      if (hasChecked) return;

      setHasChecked(true);

      try {
        console.log('[WelcomeProjectCreator] Checking welcome project creation conditions:', {
          userId: user.id,
          userEmail: user.email,
          userCreatedAt: user.created_at,
          hasWelcomeProject: hasWelcomeProject()
        });

        // 检查是否需要创建欢迎项目
        if (shouldCreateWelcomeProject(user)) {
          console.log('[WelcomeProjectCreator] New user detected, creating welcome project...');
          setIsCreating(true);

          const projectId = await createWelcomeProject();

          console.log('[WelcomeProjectCreator] Welcome project created successfully:', projectId);
          onProjectCreatedRef.current?.(projectId);
        } else {
          console.log('[WelcomeProjectCreator] Welcome project not needed or already exists');
        }
      } catch (error) {
        console.error('[WelcomeProjectCreator] Failed to create welcome project:', error);
        onErrorRef.current?.(error as Error);
      } finally {
        setIsCreating(false);
      }
    };

    handleWelcomeProjectCreation();
  }, [user, loading, hasChecked]); // 移除回调函数依赖，使用 ref 引用

  // 这是一个逻辑组件，不渲染任何 UI
  return null;
};

export default WelcomeProjectCreator;
